import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { CopyButton } from '@/components/ui/copy-button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { clientSetups } from '@/features/personal-connections/client-setup';
import { PersonalConnection } from '@/services/personal-connections/types';

/**
 * Optional help for using the provider's own MCP server from ChatGPT,
 * Claude, Cursor or another client. Separate from connecting it in Capitol:
 * it changes nothing here, and every snippet is a placeholder.
 */
export const ClientSetupPanel = ({
  connection,
}: {
  connection: PersonalConnection;
}) => {
  const [open, setOpen] = useState(false);
  const setups = clientSetups(connection);
  if (!setups.length) return null;

  return (
    <div
      className='rounded-md border'
      data-testid={`client-setup-${connection.provider}`}
    >
      <button
        type='button'
        className='flex w-full items-center gap-2 p-3 text-left text-sm font-medium'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown className='size-4' />
        ) : (
          <ChevronRight className='size-4' />
        )}
        Use {connection.name} from another AI client
      </button>
      {open && (
        <div className='border-t p-3 text-sm'>
          <p className='mb-3 text-muted-foreground'>
            Optional, and separate from Capitol: these steps connect{' '}
            {connection.name}&apos;s own server to another app with your own
            {connection.name} account.
          </p>
          <Tabs defaultValue={setups[0].client}>
            <TabsList>
              {setups.map((setup) => (
                <TabsTrigger key={setup.client} value={setup.client}>
                  {setup.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {setups.map((setup) => (
              <TabsContent
                key={setup.client}
                value={setup.client}
                className='flex flex-col gap-3 pt-2'
              >
                <p className='text-xs text-muted-foreground'>
                  {setup.methodLabel}
                </p>
                <ol className='list-decimal space-y-1 pl-5'>
                  {setup.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                {setup.snippets.map((snippet) => (
                  <div key={snippet.id} className='flex flex-col gap-1'>
                    <div className='flex items-center justify-between text-xs text-muted-foreground'>
                      <span>{snippet.label}</span>
                      <CopyButton text={snippet.text} size='small' />
                    </div>
                    <pre
                      className='overflow-x-auto rounded bg-muted p-2 text-xs'
                      data-testid={`snippet-${connection.provider}-${setup.client}-${snippet.id}`}
                    >
                      {snippet.text}
                    </pre>
                  </div>
                ))}
                <p className='text-xs text-muted-foreground'>
                  Try: &ldquo;{setup.testPrompt}&rdquo;
                </p>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}
    </div>
  );
};
